import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type MouseEvent,
  type PropsWithChildren,
} from 'react';

import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

import {
  footerInfoItems,
  footerPolicyDocuments,
  footerPolicyLinks,
  headerAssets,
} from '@/features/home/data';
import useBodyScrollLock from '@/features/home/hooks/useBodyScrollLock';
import { useSiteMenuGroups } from '@/features/home/useSiteMenuGroups';
import { useAppLocale } from '@/i18n/useAppLocale';
import { routePaths } from '@/routes/routeRegistry';
import { classNames } from '@/utils/classNames';

import type { BusinessInquiryPrivacyPolicy, SiteLink } from '../types';

import styles from './SiteFooter.module.scss';

interface SiteFooterProps {
  className?: string;
}

const companyInfoItems = footerInfoItems.filter((item) => {
  return item.label === '상호' || item.label === '대표자' || item.label === '주소';
});

const businessInfoItems = footerInfoItems.filter((item) => {
  return (
    item.label === 'TEL' ||
    item.label === 'FAX' ||
    item.label === 'E-MAIL' ||
    item.label === '사업자등록번호'
  );
});

const footerPolicyDocumentMap: Record<string, BusinessInquiryPrivacyPolicy> = footerPolicyDocuments;
const footerInfoLabelKeys: Record<string, string> = {
  'E-MAIL': 'E_MAIL',
  FAX: 'FAX',
  TEL: 'TEL',
  대표자: 'representative',
  사업자등록번호: 'businessRegistrationNumber',
  상호: 'companyName',
  주소: 'address',
};
const footerPolicyLabelKeys: Record<string, string> = {
  개인정보처리방침: 'privacy',
  이메일무단수집거부: 'emailRefusal',
  이용약관: 'terms',
};

interface FooterMenuLinkProps {
  className?: string;
  link: SiteLink;
}

const handlePlaceholderLinkClick = (
  event: MouseEvent<HTMLAnchorElement>,
  isPlaceholder?: boolean,
) => {
  if (!isPlaceholder) {
    return;
  }

  event.preventDefault();
};

const FooterMenuLink = ({ children, className, link }: PropsWithChildren<FooterMenuLinkProps>) => {
  const { localizePath } = useAppLocale();
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    handlePlaceholderLinkClick(event, link.isPlaceholder);
  };

  if (link.to) {
    return (
      <Link
        aria-disabled={link.isPlaceholder || undefined}
        className={className}
        onClick={handleClick}
        target={link.target}
        to={localizePath(link.to)}
      >
        {children}
      </Link>
    );
  }

  return (
    <a
      aria-disabled={link.isPlaceholder || undefined}
      className={className}
      href={link.href}
      onClick={handleClick}
      target={link.target}
    >
      {children}
    </a>
  );
};

const SiteFooter = ({ className }: SiteFooterProps) => {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('footer');
  const menuGroups = useSiteMenuGroups();
  const adminEntryClicksRef = useRef<number[]>([]);
  const [activePolicyLabel, setActivePolicyLabel] = useState<string | null>(null);
  const activePolicyDocument = activePolicyLabel
    ? footerPolicyDocumentMap[activePolicyLabel]
    : undefined;
  const footerLogoMaskStyle = {
    ['--site-logo-mask' as string]: `url(${headerAssets.logo})`,
  } as CSSProperties;

  useBodyScrollLock(Boolean(activePolicyDocument));

  useEffect(() => {
    if (!activePolicyDocument) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setActivePolicyLabel(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activePolicyDocument]);

  const handleAdminEntryClick = () => {
    const now = Date.now();
    const recentClicks = adminEntryClicksRef.current.filter((timestamp) => {
      return now - timestamp <= 2500;
    });
    recentClicks.push(now);
    adminEntryClicksRef.current = recentClicks;

    if (recentClicks.length < 5) {
      return;
    }

    adminEntryClicksRef.current = [];
    void navigate(routePaths.adminLogin);
  };

  const getInfoLabel = (label: string) => {
    const key = footerInfoLabelKeys[label];

    return key ? t(`infoLabels.${key}`) : label;
  };

  const getPolicyLabel = (label: string) => {
    const key = footerPolicyLabelKeys[label];

    return key ? t(`policies.${key}`) : label;
  };

  const getInfoValue = (label: string, value: string) => {
    const key = footerInfoLabelKeys[label];

    return key ? t(`infoValues.${key}`) : value;
  };

  return (
    <footer className={classNames(styles['footer'], className)}>
      <div className={styles['inner']}>
        <div className={styles['topRow']}>
          <ul className={styles['primaryMenuList']}>
            {menuGroups.map((group) => {
              return (
                <li key={group.id}>
                  <FooterMenuLink className={styles['primaryMenuLink']} link={group}>
                    {group.label}
                  </FooterMenuLink>
                </li>
              );
            })}
          </ul>

          <ul className={styles['policyList']}>
            {footerPolicyLinks.map((item) => {
              return (
                <li key={item.label}>
                  <button
                    className={styles['policyButton']}
                    onClick={() => {
                      setActivePolicyLabel(item.label);
                    }}
                    type='button'
                  >
                    {getPolicyLabel(item.label)}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className={styles['middleRow']}>
          <div aria-label={headerAssets.logoAlt} className={styles['logoBox']} role='img'>
            <span aria-hidden='true' className={styles['logoImage']} style={footerLogoMaskStyle} />
          </div>

          <div className={styles['infoGroupList']}>
            <ul className={styles['infoList']}>
              {companyInfoItems.map((item) => {
                return (
                  <li key={item.label}>
                    <span className={styles['infoLabel']}>{getInfoLabel(item.label)} :</span>
                    <span className={styles['infoValue']}>
                      {getInfoValue(item.label, item.value)}
                    </span>
                  </li>
                );
              })}
            </ul>

            <ul className={styles['infoList']}>
              {businessInfoItems.map((item) => {
                return (
                  <li key={item.label}>
                    <span className={styles['infoLabel']}>{getInfoLabel(item.label)} :</span>
                    <span className={styles['infoValue']}>
                      {getInfoValue(item.label, item.value)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        <div className={styles['bottomRow']}>
          <button
            className={styles['copyrightTrigger']}
            onClick={handleAdminEntryClick}
            type='button'
          >
            {t('copyright')}
          </button>
          <a href='https://www.newzeststudio.com' rel='noreferrer' target='_blank'>
            {t('builtBy')}
          </a>
        </div>
      </div>

      {activePolicyDocument ? (
        <div
          className={styles['policyModalOverlay']}
          onClick={() => {
            setActivePolicyLabel(null);
          }}
        >
          <div
            aria-labelledby='footer-policy-modal-title'
            aria-modal='true'
            className={styles['policyModalPanel']}
            data-lenis-prevent=''
            onClick={(event) => {
              event.stopPropagation();
            }}
            onTouchMove={(event) => {
              event.stopPropagation();
            }}
            onWheel={(event) => {
              event.stopPropagation();
            }}
            role='dialog'
          >
            <div className={styles['policyModalHeader']}>
              <h3 className={styles['policyModalTitle']} id='footer-policy-modal-title'>
                {activePolicyDocument.title}
              </h3>
              <button
                aria-label={`${activePolicyDocument.title} ${tCommon('close')}`}
                autoFocus
                className={styles['policyModalCloseButton']}
                onClick={() => {
                  setActivePolicyLabel(null);
                }}
                type='button'
              >
                ×
              </button>
            </div>

            <div className={styles['policyModalBody']} data-lenis-prevent=''>
              <p className={styles['policyModalEffectiveDate']}>
                {t('effectiveDate')}: {activePolicyDocument.effectiveDate}
              </p>

              {activePolicyDocument.intro.map((paragraph) => {
                return (
                  <p className={styles['policyModalParagraph']} key={paragraph}>
                    {paragraph}
                  </p>
                );
              })}

              {activePolicyDocument.sections.map((section) => {
                return (
                  <section className={styles['policyModalSection']} key={section.id}>
                    <h4 className={styles['policyModalSectionTitle']}>{section.title}</h4>
                    {section.paragraphs?.map((paragraph) => {
                      return (
                        <p className={styles['policyModalParagraph']} key={paragraph}>
                          {paragraph}
                        </p>
                      );
                    })}
                    {section.items?.length ? (
                      <ul className={styles['policyModalList']}>
                        {section.items.map((policyItem) => {
                          return (
                            <li className={styles['policyModalListItem']} key={policyItem}>
                              {policyItem}
                            </li>
                          );
                        })}
                      </ul>
                    ) : null}
                  </section>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
    </footer>
  );
};

export default SiteFooter;
